import { createRng, type CardInstance, type RunTallies, type StageDef } from "@reagent/sim";
import { CARDS, ERA_1_POOL, STARTER_DECK, pairingStages, type EraPoolDef } from "@reagent/content";
import type { RunChoice, RunState, StageResult } from "./types";
import { composeStage } from "./upgrades";

/**
 * The run: a sequence of stages played with one evolving deck.
 *
 * Every function here is pure — `RunState` in, `RunState` out — so a run can
 * be replayed, serialized, diffed, and tested without a browser. The store in
 * the game is a thin holder for the current value.
 */

/**
 * Placeholder payout rates (`02-cards-and-economy.md` §5), calibrated against
 * the 8 shipped worked solutions' old-formula totals (`units×10 + purity tier
 * + spareTurns×15 − wasted×3`, `round(total×0.6)+20`) — see the spec's
 * "Notes / decisions" for the old-vs-new derivation. Every one of these needs
 * playtest confirmation (0026 Open Question 1).
 */
const YIELD_CREDITS_AT_FULL_QUOTA = 40;
const PURITY_CREDITS_AT_FULL_PURITY = 40;
const SPEED_CREDITS_PER_SPARE_TURN = 10;
const VALORIZATION_CREDITS_PER_UNIT = 5;
const DUMPING_FINE_PER_WASTED_UNIT = 2;
// Placeholder rate for venting units; kept 2:1 to dumping (spec 0032, needs playtest).
const VENT_FINE_PER_UNIT = Math.floor(DUMPING_FINE_PER_WASTED_UNIT / 2);

type PairKey = `${string} ${string}`;
const pairingCache = new WeakMap<EraPoolDef, Map<PairKey, readonly StageDef[]>>();

/**
 * The three stages composed from the run's pick; none while still picking.
 * Composition validates, so it is cached per (pool, pair) rather than redone
 * on every lookup.
 */
export function runStages(run: RunState, pool: EraPoolDef = ERA_1_POOL): readonly StageDef[] {
  if (run.pathways === null) return [];
  const [first, second] = run.pathways;
  const key: PairKey = `${first} ${second}`;
  let byPair = pairingCache.get(pool);
  if (!byPair) pairingCache.set(pool, (byPair = new Map()));
  let stages = byPair.get(key);
  if (!stages) byPair.set(key, (stages = pairingStages(pool, first, second)));
  return stages;
}

/**
 * A per-stage seed derived from the run's. Mixing in the index means stage 3
 * of run 7 always deals the same hand, while stages within a run differ —
 * which is what "seed + choices reproduces the run" requires.
 */
export function stageSeed(runSeed: number, stageIndex: number): number {
  return (Math.imul(runSeed >>> 0, 0x9e3779b1) + Math.imul(stageIndex + 1, 0x85ebca6b)) >>> 0;
}

/**
 * Minted with meta's own `r` prefix (disjoint from content's `d` and the
 * engine's `h`), so a run card's id can never collide with a card the engine
 * mints mid-stage or one `freshCardInstances` mints for a standalone stage.
 */
function mintDeck(cardIds: readonly string[]): CardInstance[] {
  return cardIds.map(
    (cardId, i): CardInstance => ({
      instanceId: `r${i}`,
      cardId,
      acquiredStageIndex: 0,
      boundNodeId: null,
      isContaminated: false,
    }),
  );
}

export function startRun(seed: number, pool: EraPoolDef = ERA_1_POOL): RunState {
  // A run always begins in the pick: its deck is the starter deck plus both
  // packages, so it cannot exist before the pathways do. A pool with nothing to
  // pick between is a programmer error now the legacy stage list is gone (spec 0037).
  if (pool.pathways.length < 2) {
    throw new Error(`startRun: pool ${pool.eraId} has ${pool.pathways.length} pathways; a run needs two to pick from`);
  }
  return {
    seed: seed >>> 0,
    stageIndex: 0,
    status: "picking",
    pathways: null,
    deck: [],
    nextInstanceSeq: 0,
    upgrades: [],
    credits: 0,
    results: [],
    choices: [],
  };
}

/**
 * Commits the pathway pick: the deck becomes the starter deck plus both
 * pathways' packages, and the run begins. An invalid pick returns the run
 * unchanged. No RNG is involved, so the pick cannot move `shopRng`/`stageSeed`.
 */
export function pickPathways(
  run: RunState,
  first: string,
  second: string,
  pool: EraPoolDef = ERA_1_POOL,
): RunState {
  if (run.status !== "picking" || first === second) return run;
  const a = pool.pathways.find((p) => p.id === first);
  const b = pool.pathways.find((p) => p.id === second);
  if (!a || !b) return run;
  const deck = mintDeck([...STARTER_DECK, ...a.package, ...b.package]);
  return recordChoice(
    { ...run, status: "inProgress", pathways: [first, second], deck, nextInstanceSeq: deck.length },
    { type: "pickPathways", first, second },
  );
}

/** The stage definition the player currently faces, upgrades and deck applied. */
export function currentStage(run: RunState, pool: EraPoolDef = ERA_1_POOL): StageDef | null {
  // A picking run has no deck and no pathways yet: there is no stage to face.
  if (run.status === "picking") return null;
  const base = runStages(run, pool)[run.stageIndex];
  if (!base) return null;
  return composeStage(base, run, pool);
}

export function isLastStage(run: RunState, pool: EraPoolDef = ERA_1_POOL): boolean {
  return run.stageIndex >= runStages(run, pool).length - 1;
}

export interface StagePayout {
  readonly yieldCredits: number;
  readonly purityCredits: number;
  readonly speedCredits: number;
  readonly valorizationCredits: number;
  readonly dumpingFine: number;
  /** Subtracted like `dumpingFine` (spec 0032). */
  readonly ventFine: number;
  /** The four credit components minus the fines, floored at 0. */
  readonly total: number;
}

export function payoutFor(tallies: RunTallies, cleared: boolean): StagePayout {
  const yieldFraction =
    tallies.targetUnits > 0 ? Math.min(tallies.scoredUnits, tallies.targetUnits) / tallies.targetUnits : 0;
  const yieldCredits = Math.round(yieldFraction * YIELD_CREDITS_AT_FULL_QUOTA);
  const purityCredits = Math.round((tallies.averagePurity / 100) * PURITY_CREDITS_AT_FULL_PURITY);
  // Spare turns only pay out on a clear — otherwise stopping early would score
  // better than finishing the job (mirrors the old scoring.ts rule).
  const spareTurns = cleared ? Math.max(0, tallies.maxTurns - tallies.turnsUsed) : 0;
  const speedCredits = spareTurns * SPEED_CREDITS_PER_SPARE_TURN;
  const valorizationCredits = tallies.valorizedUnits * VALORIZATION_CREDITS_PER_UNIT;
  const dumpingFine = tallies.wastedUnits * DUMPING_FINE_PER_WASTED_UNIT;
  const ventFine = tallies.ventedUnits * VENT_FINE_PER_UNIT;
  const total = Math.max(0, yieldCredits + purityCredits + speedCredits + valorizationCredits - dumpingFine - ventFine);
  return { yieldCredits, purityCredits, speedCredits, valorizationCredits, dumpingFine, ventFine, total };
}

/**
 * Sums the Catalyst Deactivation-style "at end of stage if unstripped" fee
 * over every surviving copy in `deck` (spec 0007 §4). Generic over whatever
 * hazard cards the catalogue marks `maintenanceFee` — today only one id
 * qualifies, but nothing here hardcodes it.
 */
function maintenanceFeeFor(deck: readonly CardInstance[]): number {
  let total = 0;
  for (const instance of deck) {
    const hazard = CARDS[instance.cardId]?.hazard;
    if (hazard?.kind === "maintenanceFee") total += hazard.credits;
  }
  return total;
}

/**
 * Records how a stage went and moves the run on.
 *
 * A cleared stage opens the shop (or wins the run, if it was the last).
 * A failed one ends the run — this is a roguelike, and a stage you can retry
 * for free is not a stage you have to think about.
 *
 * `deckCards`, when given, is `GridSnapshot.deckCards` as the stage ended:
 * draw + hand + discard, which is a complete accounting of `run.deck` at
 * stage start plus every card injected mid-stage minus every card exiled
 * mid-stage (spec 0007's "Stage-end deck reconciliation"). Reconciling it is
 * a wholesale replacement, not a diff — the snapshot already *is* the full
 * multiset. It is applied unconditionally, cleared or failed (resolved open
 * question 4), and the maintenance fee is charged against the reconciled
 * deck (resolved open question 3), before the three-way status branch below.
 */
export function finishStage(
  run: RunState,
  tallies: RunTallies,
  cleared: boolean,
  deckCards?: readonly CardInstance[],
  setupSpentCredits?: number,
  pool: EraPoolDef = ERA_1_POOL,
): RunState {
  const base = runStages(run, pool)[run.stageIndex];
  if (!base || run.status !== "inProgress") return run;

  const payout = payoutFor(tallies, cleared);
  const creditsEarned = payout.total;
  const minted = deckCards ? adoptInjectedCards(run, deckCards) : null;
  const deck = minted ? minted.deck : run.deck;
  const maintenanceFeePaid = deckCards ? maintenanceFeeFor(deck) : 0;
  const credits = Math.max(0, run.credits + creditsEarned - maintenanceFeePaid - (setupSpentCredits ?? 0));

  const result: StageResult = {
    stageId: base.id,
    cleared,
    payout,
    creditsEarned,
    turnsUsed: tallies.turnsUsed,
    purity: tallies.averagePurity,
    // Omitted rather than 0 when no reconciliation ran, so a 3-arg call's
    // StageResult is byte-identical to before this task.
    ...(deckCards ? { maintenanceFeePaid } : {}),
  };

  const results = [...run.results, result];
  // Only override `deck` when it was actually reconciled — the 3-arg shape
  // must leave `run.deck` exactly as the existing spread did.
  const deckOverride = minted ? { deck, nextInstanceSeq: minted.nextInstanceSeq } : {};

  if (!cleared) {
    return { ...run, ...deckOverride, status: "lost", results, credits };
  }

  if (isLastStage(run, pool)) {
    return {
      ...run,
      ...deckOverride,
      status: "won",
      results,
      credits,
      stageIndex: run.stageIndex + 1,
    };
  }

  return { ...run, ...deckOverride, status: "shopping", results, credits };
}

/**
 * Gives every card that joined the deck mid-stage a run-level identity.
 *
 * The engine mints hazard cards under its own `h` counter, which starts over
 * every stage — so an `h1` carried out of stage 1 would collide with the `h1`
 * stage 2 injects (`GAPS.md` G-G15). The run is the only thing that lives
 * across stages, so it re-mints those cards under its own `r` counter and
 * stamps the stage they were actually acquired at, which the engine cannot
 * know. Anything already in `run.deck` keeps its id untouched.
 */
function adoptInjectedCards(
  run: RunState,
  deckCards: readonly CardInstance[],
): { deck: CardInstance[]; nextInstanceSeq: number } {
  const owned = new Set(run.deck.map((ci) => ci.instanceId));
  let nextInstanceSeq = run.nextInstanceSeq;
  const deck = deckCards.map((ci) =>
    owned.has(ci.instanceId)
      ? ci
      : { ...ci, instanceId: `r${nextInstanceSeq++}`, acquiredStageIndex: run.stageIndex },
  );
  return { deck, nextInstanceSeq };
}

/** Leaves the shop and starts the next stage. */
export function leaveShop(run: RunState): RunState {
  if (run.status !== "shopping") return run;
  return { ...run, status: "inProgress", stageIndex: run.stageIndex + 1 };
}

export function recordChoice(run: RunState, choice: RunChoice): RunState {
  return { ...run, choices: [...run.choices, choice] };
}

/** Total Credits earned across every stage — the run's headline number. */
export function runCredits(run: RunState): number {
  return run.results.reduce((total, r) => total + r.creditsEarned, 0);
}

/**
 * Deck composition, for the shop's "what am I actually holding" readout.
 * Sorted, so the same deck always reads the same way.
 */
export function deckCounts(
  deck: readonly CardInstance[],
): ReadonlyArray<{ cardId: string; count: number }> {
  const counts = new Map<string, number>();
  for (const instance of deck) counts.set(instance.cardId, (counts.get(instance.cardId) ?? 0) + 1);
  return [...counts.entries()]
    .map(([cardId, count]) => ({ cardId, count }))
    .sort((a, b) => (a.cardId < b.cardId ? -1 : 1));
}

/** A seeded RNG for one shop visit. Same run + same stage ⇒ same offers. */
export function shopRng(run: RunState) {
  return createRng(stageSeed(run.seed, run.stageIndex + 1000));
}
